import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui-kit/avatar';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { HugeiconsIcon } from '@hugeicons/react';
import { FilterMailIcon } from '@hugeicons/core-free-icons';
import {
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Search,
  ShieldCheck,
  User,
  UserCog,
  UserMinus,
} from 'lucide-react';
import type { OrgMember } from '../types/org-users.types';
import type { OrgRole } from '../../org-permissions/types/permissions.types';

interface MembersTableProps {
  members: OrgMember[];
  isLoading?: boolean;
  onRoleChange: (memberId: string, newRole: OrgRole) => void;
  onRemove: (member: OrgMember) => void;
  updatingMemberId: string | null;
  /** Optional content rendered next to the search input. Used to host
      the Refresh button on mobile so it sits beside search instead of
      consuming a separate header row. */
  searchAction?: React.ReactNode;
}

const roleConfig: Record<OrgRole, { icon: typeof ShieldCheck; color: string; bgClass: string }> = {
  org_admin: {
    icon: ShieldCheck,
    color: 'selise-blue',
    bgClass: 'bg-[hsl(var(--selise-blue))] text-[hsl(var(--white))]',
  },
  org_editor: {
    icon: UserCog,
    color: 'lime-green',
    bgClass: 'bg-[hsl(var(--lime-green))] text-[hsl(var(--white))]',
  },
  org_member: {
    icon: User,
    color: 'globe-grey',
    bgClass: '',
  },
};

export const MembersTable = ({
  members,
  isLoading,
  onRoleChange,
  onRemove,
  updatingMemberId,
  searchAction,
}: MembersTableProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<'all' | OrgRole>('all');
  const [filterOpen, setFilterOpen] = useState(false);

  const filteredMembers = useMemo(() => {
    let result = members;

    if (roleFilter !== 'all') {
      result = result.filter((m) => m.role === roleFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (member) =>
          member.email.toLowerCase().includes(query) ||
          (member.firstName ?? '').toLowerCase().includes(query) ||
          (member.lastName ?? '').toLowerCase().includes(query) ||
          `${member.firstName ?? ''} ${member.lastName ?? ''}`.toLowerCase().includes(query)
      );
    }

    return result;
  }, [members, searchQuery, roleFilter]);

  const getInitials = (firstName: string | null, lastName: string | null) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getRoleConfig = (role: OrgRole | null) => {
    return roleConfig[role || 'org_member'] || roleConfig.org_member;
  };

  const getRoleLabel = (role: OrgRole | null) => {
    const roleKey = role?.replace('org_', '') || 'member';
    return t(`ORGTEAM_ROLES_${roleKey.toUpperCase()}`);
  };

  const handleRoleSelect = (member: OrgMember, newRole: OrgRole) => {
    setOpenPopoverId(null);
    if (member.role !== newRole) {
      onRoleChange(member.id, newRole);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="size-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-8" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <div className="flex items-center rounded-md border border-border bg-[hsl(var(--bg))] shadow-xs px-3 py-1.5">
            <Search className="mr-2 size-4 text-globe-grey" />
            <Input
              placeholder={t('ORGTEAM_MEMBERSTABLE_SEARCHPLACEHOLDER')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-5 md:h-8 border-0 shadow-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm placeholder:text-[hsl(var(--globe-grey))]"
            />
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-2 size-7 rounded-full text-[hsl(var(--globe-grey))] hover:bg-[hsl(var(--muted))]"
                >
                  <HugeiconsIcon
                    icon={FilterMailIcon}
                    size={20}
                    className="size-4 md:size-6 text-[hsl(var(--globe-grey))]"
                  />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1">
                <div className="space-y-1">
                  <p className="px-2 py-1.5 text-xs font-medium text-[hsl(var(--globe-grey))]">
                    {t('ORGTEAM_MEMBERSTABLE_FILTERBYROLE')}
                  </p>
                  <button
                    className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-[hsl(var(--muted))] ${
                      roleFilter === 'all' ? 'bg-[hsl(var(--muted))] font-medium' : ''
                    }`}
                    onClick={() => {
                      setRoleFilter('all');
                      setFilterOpen(false);
                    }}
                  >
                    {t('ORGTEAM_MEMBERSTABLE_ALLMEMBERS')}
                  </button>
                  {(['org_admin', 'org_editor', 'org_member'] as const).map((role) => (
                    <button
                      key={role}
                      className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-[hsl(var(--muted))] ${
                        roleFilter === role ? 'bg-[hsl(var(--muted))] font-medium' : ''
                      }`}
                      onClick={() => {
                        setRoleFilter(role);
                        setFilterOpen(false);
                      }}
                    >
                      {getRoleLabel(role)}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {searchAction ? <div className="shrink-0">{searchAction}</div> : null}
      </div>

      <div className="rounded-md border border-secondary-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b-3! border-secondary-100 text-gray-500 font-nunito">
              <TableHead>{t('ORGTEAM_MEMBERSTABLE_COLUMNS_MEMBER')}</TableHead>
              <TableHead>{t('ORGTEAM_MEMBERSTABLE_COLUMNS_ROLE')}</TableHead>
              <TableHead>{t('ORGTEAM_MEMBERSTABLE_COLUMNS_JOINED')}</TableHead>
              <TableHead className="text-right w-12">
                {t('ORGTEAM_MEMBERSTABLE_COLUMNS_ACTIONS')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  <p className="text-[hsl(var(--globe-grey))]">
                    {searchQuery
                      ? t('ORGTEAM_MEMBERSTABLE_EMPTYSEARCH')
                      : t('ORGTEAM_MEMBERSTABLE_EMPTY')}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredMembers.map((member) => {
                const config = getRoleConfig(member.role);
                const RoleIcon = config.icon;
                const isUpdating = updatingMemberId === member.id;

                return (
                  <TableRow
                    key={member.id}
                    className="hover:bg-[hsl(var(--muted))]/50 border-b border-secondary-100"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="size-8">
                          <AvatarImage src={member.imageUrl || undefined} />
                          <AvatarFallback className="text-xs">
                            {getInitials(member.firstName, member.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">
                            {member.firstName || member.lastName
                              ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                              : member.email}
                          </p>
                          {(member.firstName || member.lastName) && (
                            <p className="text-xs text-[hsl(var(--globe-grey))]">{member.email}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {isUpdating ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="size-4 animate-spin" />
                          <span className="text-sm text-[hsl(var(--globe-grey))]">
                            {t('ORGTEAM_MEMBERSTABLE_UPDATING')}
                          </span>
                        </div>
                      ) : (
                        <Badge variant="default" className={config.bgClass}>
                          <RoleIcon className="size-3 mr-1" />
                          {getRoleLabel(member.role)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[hsl(var(--globe-grey))]">
                      {new Date(member.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Popover
                        open={openPopoverId === member.id}
                        onOpenChange={(open) => setOpenPopoverId(open ? member.id : null)}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            disabled={isUpdating}
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-48 p-1">
                          <div className="space-y-1">
                            <p className="px-2 py-1.5 text-xs font-medium text-[hsl(var(--globe-grey))]">
                              {t('ORGTEAM_MEMBERSTABLE_CHANGEROLE')}
                            </p>
                            {(['org_admin', 'org_editor', 'org_member'] as const).map((role) => {
                              const roleConf = roleConfig[role];
                              const RoleIconItem = roleConf.icon;
                              const isSelected = member.role === role;
                              return (
                                <button
                                  key={role}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-[hsl(var(--muted))] ${
                                    isSelected ? 'bg-[hsl(var(--muted))] font-medium' : ''
                                  }`}
                                  onClick={() => handleRoleSelect(member, role)}
                                >
                                  <RoleIconItem
                                    className={`size-4 text-[hsl(var(--${roleConf.color}))]`}
                                  />
                                  {getRoleLabel(role)}
                                  {isSelected && <ChevronRight className="size-4 ml-auto" />}
                                </button>
                              );
                            })}
                            <div className="border-t border-t-gray-300 my-1" />
                            <button
                              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-[hsl(var(--crimson))]/10 text-[hsl(var(--crimson))]"
                              onClick={() => {
                                setOpenPopoverId(null);
                                onRemove(member);
                              }}
                            >
                              <UserMinus className="size-4" />
                              {t('ORGTEAM_MEMBERSTABLE_REMOVEMEMBER')}
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
